<template>
  <article class="relative flex w-full" data-component="CardEventAndSeries">
    <AtomLink :url="url" class="group grid md:grid-cols-12 w-full rounded-3xl overflow-hidden shadow-lg hover:shadow-none transform transition duration-200">
      <section class="order-2 c-content relative row-span-1 md:col-span-6 flex">
        <div class="relative z-20 bg-white flex flex-1 flex-col justify-end px-5 pb-5 lg:pl-2 md:pb-8" :class="{ 'md:pt-5': body, 'md:pt-12 lg:pt-8': !body }">
          <div class="flex-auto">
            <h3
              v-if="title"
              class="c-content__title mr-10 md:mr-5 text-base sm:text-lg lg:text-2xl font-semibold leading-snug text-black mb-2"
              :class="{ 'md:mb-3': body, 'md:mb-5': !body }"
            >
              {{ title }}
            </h3>
            <template v-if="body">
              <div class="leading-snug text-xs lg:text-sm mr-6 mb-4">
                <div v-if="stripTags(body).length < 195" v-html="body" />
                <div v-else v-html="truncate(stripTags(body), { length: 195 })" />
              </div>
            </template>
            <ul v-if="whenDescription" class="flex flex-wrap text-sm lg:text-base font-semibold leading-tight mb-2" :class="{ 'lg:mb-4': !body }">
              <li class="py-1">{{ whenDescription }}</li>
            </ul>
            <ul class="flex flex-wrap text-sm lg:text-base font-semibold leading-tight mb-4" :class="{ 'lg:mb-6': !body }">
              <li v-if="when" class="py-1 inline-flex items-center shrink-0">
                <time :datetime="d_(when, 'iso')" class="c-card-event-and-series__time lowercase mr-2 lg:mr-5">
                  {{ d_(when, 'short-time') }}<template v-if="endDate"><span class="px-px lg:px-1">–</span>{{ d_(endDate, 'short-time') }}</template>
                </time>
              </li>
              <template v-if="location">
                <li v-if="when" role="presentation" class="my-1 border-r border-solid border-app-primary-1-red--fire-brick mr-2 lg:mr-5"></li>
                <li class="py-1">
                  <span class="mr-2 lg:mr-5">{{ location }}</span>
                </li>
              </template>
              <template v-if="minPrice">
                <li v-if="when || location" role="presentation" class="my-1 border-r border-solid border-app-primary-1-red--fire-brick mr-2 lg:mr-5"></li>
                <li class="py-1">
                  <span class="mr-2 lg:mr-5">{{ $t('card-event-and-series.card-price', { minPrice: $n(minPrice, 'currency') }) }}</span>
                </li>
              </template>
              <template v-if="streamUrl">
                <li v-if="when || location || minPrice" role="presentation" class="my-1 border-r border-solid border-app-primary-1-red--fire-brick mr-2 lg:mr-5"></li>
                <li class="py-1">
                  <span class="mr-2 lg:mr-5">{{ $t('card-event-and-series.stream-available') }}</span>
                </li>
              </template>
              <template v-if="saleIsAvailableInFuture">
                <li v-if="when || location || minPrice || streamUrl" role="presentation" class="my-1 border-r border-solid border-app-primary-1-red--fire-brick mr-2 lg:mr-5"></li>
                <li class="py-1">
                  <span class="mr-2 lg:mr-5">{{ $t('event-overview-available-from-datetime', { datetime: $d(saleAvailableFromDate, 'long-date-time') }) }}</span>
                </li>
              </template>
              <template v-if="discount">
                <li
                  v-if="when || location || minPrice || streamUrl || saleIsAvailableInFuture"
                  role="presentation"
                  class="my-1 border-r border-solid border-app-primary-1-red--fire-brick mr-2 lg:mr-5"
                ></li>
                <li class="py-1">
                  <span class="mr-2 lg:mr-5">{{ discount }}</span>
                </li>
              </template>
            </ul>
            <div v-if="sortedWorks && sortedWorks.length > 0" class="text-xs leading-tight mb-4" :class="{ 'lg:mb-5': !body }">
              <span v-if="sortedWorks.length > 1" class="block mb-1 italic">{{ t_('including') }}</span>
              <ul class="list-inside list-none">
                <li v-for="(work, index) in sortedWorks.slice(-2).reverse()" :key="index" class="mb-1 c-content-list-item">
                  <!-- The {{ ' ' }} below is a real space rather than a margin, so it survives copy-paste and
                       screen readers: Vue 3's compiler drops the newline between the spans that provided it. -->
                  <span v-if="work.composers.length > 0" class="font-medium">{{ work.composers[0].title }}</span
                  >{{ ' ' }}<span class="italic">{{ work.parent && work.parent.id ? `${work.title} ${work.parent.title}` : work.title }}</span>
                </li>
              </ul>
            </div>
            <AtomAnimationsCircleLoader v-if="loading" class="w-12 h-12" />
          </div>
          <footer class="flex flex-wrap">
            <template v-if="url">
              <MoleculeButton class="lg:hidden w-full" background-color="bg-app-primary-1-red--torch group-hover:bg-black" size="large">
                {{ saleIsAvailable ? $t('card-event-and-series.information-and-order') : $t('card-event-and-series.information') }}
              </MoleculeButton>
              <MoleculeButton class="hidden lg:flex w-full" background-color="bg-app-primary-1-red--torch group-hover:bg-black">
                {{ saleIsAvailable ? $t('card-event-and-series.information-and-order') : $t('card-event-and-series.information') }}
              </MoleculeButton>
            </template>
            <div v-if="saleIcon && saleTemplate" class="flex items-center shrink-0 text-app-primary-1-red--torch pt-4">
              <AtomIcon :icon="saleIcon" classes="w-4 lg:w-5 mr-1 shrink-0" />
              <p class="pt-2 pb-1 text-xs lg:text-lg leading-none font-semibold">{{ $t(saleTemplate) }}</p>
            </div>
            <div v-else-if="concertStatusLabel && concertStatusIcon" class="flex items-center shrink-0 text-app-primary-1-red--torch">
              <AtomIcon :icon="concertStatusIcon" classes="w-4 lg:w-5 mr-1 shrink-0" />
              <p class="pt-2 pb-1 text-xs lg:text-lg leading-none font-semibold">{{ concertStatusLabel }}</p>
            </div>
          </footer>
        </div>
      </section>
      <figure class="order-1 row-span-1 md:col-span-6 relative">
        <span
          v-if="label"
          class="absolute z-10 bg-app-primary-1-red--fire-brick top-0 left-0 rounded-br-3xl text-white font-semibold text-sm lg:text-lg leading-none px-5 py-3 lg:px-8 lg:py-3"
        >
          {{ label }}
        </span>
        <span
          v-else-if="when"
          class="absolute z-10 bg-app-primary-1-red--fire-brick top-0 left-0 rounded-br-3xl text-white font-semibold text-sm lg:text-lg leading-none px-5 py-3 lg:px-8 lg:py-3"
        >
          {{ d_(when, 'long-date') }}
        </span>
        <button
          v-if="eventId && canBeFavorited"
          class="absolute z-10 top-0 right-0 hidden md:block mt-5 mr-16 text-white"
          @click.stop.prevent="favoritesEnabled ? favoritesToggleEventId(eventId) : favoritesOpenOverlay(eventId)"
        >
          <AtomIconBookmarkFilled v-if="favoriteEventIConcertIds.includes(eventId)" class="w-8 h-8 mx-2" />
          <AtomIconBookmark v-else class="w-8 h-8 mx-2" />
        </button>
        <div
          v-if="audioWork.audio"
          class="absolute z-10 bottom-0 left-0 rounded-br-3xl text-white font-semibold text-sm mb-10 md:mb-0 lg:text-lg leading-none pl-5 pb-4 lg:pb-3 w-full flex items-center"
          :class="{ isPlaying: '' }"
        >
          <div class="justify-center inline-block content-end">
            <AtomIcon
              :icon="playerStates[audioWork.id] ? 'app-pause' : 'app-play'"
              classes="hover:text-app-primary-1-orange--bittersweet cursor-pointer transform transition duration-400 w-10 shrink-0"
              @click.stop.prevent="() => toggleAudio()"
            />
          </div>
          <div class="flex justify-center lg:gap-3 w-3/4 h-[calc(50%-30px)]" :class="{ 'opacity-100': isPlaying, 'opacity-0': !isPlaying }">
            <AtomAnimationsRecommendationEqualiser class="!p-1 md:!p-3 !w-1/5 !m-0 min-h-14" />
            <span class="font-bold self-center">Luisteren</span>
          </div>
        </div>
        <MoleculePicture class="block w-full h-full object-cover group-hover:scale-110 group-focus:scale-110 transform transition duration-200" :image="image" />
        <div
          class="absolute inset-0 bg-gradient-to-t from-[#000] to-transparent transition-opacity duration-200"
          :class="{ 'opacity-100': isPlaying, 'opacity-0': !isPlaying }"
        ></div>
      </figure>
    </AtomLink>
    <div
      v-if="eventId && canBeFavorited"
      class="md:hidden z-40 text-white absolute top-4 right-4 xl:top-6 hover:cursor-pointer xl:right-6 md:text-app-primary-1-orange--bittersweet"
      @click="favoritesEnabled ? favoritesToggleEventId(eventId) : favoritesOpenOverlay(eventId)"
    >
      <AtomIconBookmarkFilled v-if="favoriteEventIConcertIds.includes(eventId)" class="w-8 h-8 mx-2" />
      <AtomIconBookmark v-else class="w-8 h-8 mx-2" />
    </div>
    <template v-for="work in works">
      <div v-if="work.audio" :key="work.id">
        <audio :id="work.id" :key="work.id" hidden>
          <source :src="work.audio.url" :type="work.audio.mimeType" />
        </audio>
      </div>
    </template>
  </article>
</template>

<script setup lang="ts">
import { stripTags, truncate } from '@zicht/nuxt-util'

const props = defineProps({
  body: { type: String, required: false, default: undefined },
  endDate: { type: [String, Date], required: false, default: undefined },
  image: {
    type: Object as PropType<Record<string, any>>,
    required: false,
    default: () => ({ _smUrl: '/images/fallback/345x206.png', _smWidth: 908, _smHeight: 545, _mdUrl: '/images/fallback/463x280.png', _mdWidth: 947, _mdHeight: 573 }),
  },
  imgAlt: { type: String, required: false, default: undefined },
  label: { type: String, required: false, default: undefined },
  discount: { type: String, required: false, default: undefined },
  location: { type: String, required: false, default: undefined },
  streamUrl: { type: String, required: false, default: undefined },
  title: { type: String, required: false, default: undefined },
  url: { type: String, required: false, default: undefined },
  when: { type: [String, Date], required: false, default: undefined },
  whenDescription: { type: String, required: false, default: undefined },
  works: { type: Array as PropType<any[]>, required: false, default: () => [] },

  // The card can be used as a placeholder while the underlying data is loading
  loading: { type: Boolean, required: false, default: false },

  // Should we show "favorite" functionality?
  canBeFavorited: { type: Boolean, required: false, default: true },

  // concert status from "IConcert"
  concertStatus: { type: String, required: false, default: '' },
  eventId: { type: Number, required: false, default: undefined },
  // Price information from `setupTixPricing`
  tixPricing: { type: Object as PropType<Record<string, any>>, required: false, default: () => ({}) },
  // Sale information from `setupTixSale`
  tixSale: { type: Object as PropType<Record<string, any>>, required: false, default: () => ({}) },
})

const minPrice = computed(() => props.tixPricing?.minPrice?.value)
const { saleIcon, saleTemplate, saleIsAvailable, saleIsAvailableInFuture, saleAvailableFromDate } = props.tixSale
const favoritesStore = useFavoritesStore()
const audioPlayerStore = useAudioPlayerStore()

const { t_, d_ } = setupTranslationPrefixer('CardEventAndSeries')
const { concertStatusLabel, concertStatusIcon } = setupConcertStatus(props.concertStatus, 'card-event-and-series')

const audioWork = computed(
  () =>
    props.works?.slice().find(({ audio, mainFragment }) => audio?.url && mainFragment) ||
    props.works
      ?.slice()
      .reverse()
      .find(({ audio }) => audio?.url) ||
    {},
)

const playerStates = computed(() => audioPlayerStore.playerStates)
const isPlaying = computed(() => playerStates.value[audioWork.value.id])

const favoritesEnabled = computed(() => (props.canBeFavorited ? favoritesStore.enabled : false))
const favoriteEventIConcertIds = computed(() => (props.canBeFavorited ? favoritesStore.eventIConcertIds : []))

function favoritesToggleEventId(eventIConcertId: number) {
  favoritesStore.toggleEventIConcertId(eventIConcertId)
}

function favoritesOpenOverlay(eventIConcertId: number) {
  favoritesStore.openOverlayAndStoreId(eventIConcertId)
}

function toggleAudio() {
  audioPlayerStore.toggleAudio(audioWork.value.id)
}

onBeforeUnmount(() => (isPlaying.value === true ? audioPlayerStore.toggleAudio(audioWork.value.id) : null))

const sortedWorks = computed(() => [...props.works].filter((work) => !work.encore).sort((a, b) => a.featured - b.featured))
</script>

<style lang="scss" scoped>
$side-size-svg: 49px;

.c-content {
  @apply z-10;
  padding-top: calc(#{$side-size-svg} - 1px);
  margin-top: calc(-1 * (#{$side-size-svg} - 1px));

  @screen md {
    @apply mt-0 pt-0;
  }

  &::before {
    content: '';
    background-image: url('/images/brand/BrandLyreCardHorizontal.svg');
    width: 84px;
    height: $side-size-svg;
    @apply absolute top-0 right-0 bg-no-repeat bg-cover;

    @screen md {
      @apply right-auto left-0;
      background-image: url('/images/brand/BrandLyreCardVertical.svg');
      width: $side-size-svg;
      height: 84px;
      padding-left: calc(#{$side-size-svg} - 1px);
      margin-left: calc(-1 * (#{$side-size-svg} - 1px));
    }
  }

  &::after {
    content: '';
    width: calc(100% - 84px + 1px);
    height: $side-size-svg;
    @apply absolute top-0 left-0 bg-white;

    @screen md {
      @apply top-auto bottom-0;
      width: $side-size-svg;
      height: calc(100% - 84px);
      margin-left: calc(-1 * (#{$side-size-svg} - 1px));
    }
  }
}

.c-content__title {
  margin-top: calc(-1 * theme('spacing.6'));

  @screen md {
    @apply mt-0;
  }
}

.c-content-list-item {
  position: relative;
  padding-left: 12px;

  &::marker {
    display: none;
  }
  &::before {
    content: '·';
    position: absolute;
    top: -2px;
    left: 0;
    font-size: 20px;
    line-height: 1;
  }
}

.c-card-event-and-series__time {
  word-spacing: -4px;
}
</style>
